import { useEffect, useRef, useState } from "react";
import { Building2, Bell, CalendarDays, TrendingUp, ShieldCheck, FileCheck2, PiggyBank, Check } from "lucide-react";
import BrandLogo from "@/components/site/BrandLogo";
import { annualPriceForLots, proAnnualForLots } from "@/lib/pricing";

// La copropriété de démonstration compte 10 lots principaux. L'économie affichée dans
// l'encart flottant en est DÉRIVÉE (tarif TyCopro vs syndic professionnel au tarif
// médian du marché) : elle ne peut donc pas se désynchroniser de la grille.
const DEMO_LOTS = 10;
const DEMO_SAVING = proAnnualForLots(DEMO_LOTS) - annualPriceForLots(DEMO_LOTS);

/**
 * Aperçu produit du hero : une « fenêtre d'app » recréée en HTML/Tailwind (aucune
 * image, aucun framer-motion) reproduisant le tableau de bord TyCopro, entourée de
 * deux encarts flottants qui résument les moats (sécurité juridique AR24 + économies).
 * Les animations (remplissage de la barre, count-up, flottement) respectent
 * prefers-reduced-motion.
 */

// Détecte prefers-reduced-motion sans dépendance externe.
function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReduced(mq.matches);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);
    return reduced;
}

// Compteur animé 0 → target (requestAnimationFrame, easeOutCubic). Valeur finale
// immédiate si reduced-motion ou tant que `start` est faux.
function useCountUp(target, { duration = 1200, start = false, reduced = false } = {}) {
    const [value, setValue] = useState(0);
    const raf = useRef(0);
    useEffect(() => {
        if (reduced) {
            setValue(target);
            return;
        }
        if (!start) return;
        let t0 = null;
        const tick = (t) => {
            if (t0 === null) t0 = t;
            const p = Math.min((t - t0) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(target * eased));
            if (p < 1) raf.current = requestAnimationFrame(tick);
        };
        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, [target, duration, start, reduced]);
    return value;
}

function Stat({ icon: Icon, label, value, sub, tone = "slate" }) {
    const tones = {
        primary: "text-primary-700 bg-primary-50",
        emerald: "text-emerald-600 bg-emerald-50",
        slate: "text-slate-600 bg-slate-100",
    };
    return (
        <div className="rounded-xl border border-slate-200 p-2.5">
            <div className="flex items-center gap-1.5">
                <span className={`grid h-5 w-5 place-items-center rounded-md ${tones[tone]}`}>
                    <Icon className="h-3 w-3" strokeWidth={1.75} />
                </span>
                <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</span>
            </div>
            <div className="mt-1.5 font-heading text-sm font-semibold text-slate-900">{value}</div>
            <div className="text-[10px] text-slate-500">{sub}</div>
        </div>
    );
}

export default function HeroAppPreview() {
    const reduced = usePrefersReducedMotion();
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setMounted(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const saving = useCountUp(DEMO_SAVING, { start: mounted, reduced });
    const barWidth = reduced || mounted ? "100%" : "0%";
    const barTransition = reduced ? "" : "transition-[width] duration-1000 ease-out";
    const floatClass = reduced ? "" : "animate-float";

    return (
        <div
            data-testid="hero-visual"
            role="img"
            aria-label="Aperçu de l'application TyCopro : tableau de bord d'une copropriété à jour"
            className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
            {/* Halo derrière la fenêtre */}
            <div
                aria-hidden="true"
                className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-br from-primary-300/30 via-primary-200/20 to-transparent blur-2xl"
            />

            {/* Fenêtre d'app */}
            <div
                aria-hidden="true"
                className="animate-fade-in-up relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_40px_80px_-30px_rgba(67,56,202,0.45)] ring-1 ring-white"
                style={{ animationDelay: "120ms" }}
            >
                {/* Top chrome */}
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <div className="flex items-center gap-2">
                        <BrandLogo
                            variant="color"
                            alt=""
                            className="h-6 w-auto"
                            loading="lazy"
                            fallback={
                                <>
                                    <div className="grid h-7 w-7 place-items-center rounded-lg bg-primary-700 text-white">
                                        <Building2 className="h-4 w-4" strokeWidth={1.75} />
                                    </div>
                                    <span className="font-heading text-sm font-semibold text-slate-900">TyCopro</span>
                                </>
                            }
                        />
                        <span className="ml-1 rounded-full border border-primary-100 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                            Résidence Les Tilleuls
                        </span>
                    </div>
                    <div className="relative text-slate-300">
                        <Bell className="h-4 w-4" strokeWidth={1.75} />
                        <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary-500" />
                    </div>
                </div>

                {/* Corps : tableau de bord condensé */}
                <div className="space-y-3 p-4 sm:p-5">
                    <div className="flex items-baseline justify-between">
                        <div>
                            <div className="font-heading text-base font-semibold text-slate-900">Tableau de bord</div>
                            <div className="text-[11px] text-slate-500">Exercice 2026</div>
                        </div>
                        <span className="rounded-lg bg-primary-700 px-2.5 py-1 text-[10px] font-medium text-white">+ Créer une AG</span>
                    </div>

                    {/* Statut vert */}
                    <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                        <span className="grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-white">
                            <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        </span>
                        <span className="text-[11px] font-medium text-emerald-800">Tout est à jour, aucune action urgente</span>
                    </div>

                    {/* Score de santé */}
                    <div className="rounded-xl border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-medium text-slate-500">Score de santé</span>
                            <span className="font-heading text-sm font-semibold text-slate-900">
                                100<span className="text-[11px] font-normal text-slate-500">/100</span>
                            </span>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                            <div
                                className={`h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 ${barTransition}`}
                                style={{ width: barWidth }}
                            />
                        </div>
                    </div>

                    {/* 4 cartes clés */}
                    <div className="grid grid-cols-2 gap-2.5">
                        <Stat icon={CalendarDays} label="Prochaine AG" value="17/07/2026" sub="Dans 28 jours" tone="primary" />
                        <Stat icon={TrendingUp} label="Dépenses 2026" value="28 500 €" sub="Budget 42 000 €" tone="slate" />
                        <Stat icon={ShieldCheck} label="Impayés" value="0" sub="Aucun impayé" tone="emerald" />
                        <Stat icon={FileCheck2} label="Règlement" value="À jour" sub="Analysé" tone="emerald" />
                    </div>
                </div>
            </div>

            {/* Encart flottant : preuve AR24 (sécurité juridique) — masqué en mobile */}
            <div
                aria-hidden="true"
                className={`absolute -top-4 -right-3 hidden sm:block sm:-right-6 ${floatClass}`}
                style={{ animationDelay: "0.4s" }}
            >
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-[0_18px_40px_-16px_rgba(67,56,202,0.45)]">
                    <span className="grid h-7 w-7 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                        <FileCheck2 className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div className="leading-tight">
                        <div className="text-[11px] font-semibold text-slate-900">Convocation envoyée</div>
                        <div className="text-[10px] text-slate-500">Preuve AR24 horodatée</div>
                    </div>
                </div>
            </div>

            {/* Encart flottant : économies (argent) — masqué en mobile */}
            <div
                aria-hidden="true"
                className={`absolute -bottom-5 -left-3 hidden sm:block sm:-left-6 ${floatClass}`}
                style={{ animationDelay: "1.6s" }}
            >
                <div className="flex items-center gap-2.5 rounded-2xl bg-gradient-to-br from-primary-700 to-primary-600 px-3.5 py-2.5 text-white shadow-[0_20px_45px_-15px_rgba(67,56,202,0.7)]">
                    <span className="grid h-8 w-8 place-items-center rounded-xl bg-white/15">
                        <PiggyBank className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div className="leading-none">
                        <div className="font-heading text-base font-semibold">+{saving.toLocaleString("fr-FR")} €</div>
                        <div className="mt-0.5 text-[10px] leading-tight text-primary-100">par an économisés</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
